import React, { useEffect, useState } from "react";
import { API } from "../../../config";
import useCart from "./useCart";
import CreateOrder from "../orders/CreateOrder";
import OrderSuccessModal from "../orders/OrderSuccessModal";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import CustomLoading from "../../../utils/LoadingIndicator/CustomLoading";

function AuthenticatedCart() {
  const {
    cartItems,
    isGettingCart,
    isErrCart,
    isUpdatingQuantity,
    updateItemQuantityFn,
    removeItemFromCartFn,
    isRemovingProduct,
    removingItemId,
    clearCartFn,
    isClearingCart,
    updatingItemId,
  } = useCart();

  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleQuantityUpdate = (productId, newQuantity) => {
    try {
      updateItemQuantityFn({ productId, quantity: newQuantity });
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  };

  const handleRemoveItem = (productId) => {
    try {
      removeItemFromCartFn(productId);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const handleClearCart = () => {
    try {
      clearCartFn();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  if (isGettingCart) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CustomLoading size="2rem" />
      </div>
    );
  }

  if (isErrCart) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-2xl font-semibold text-gray-600">
          Some error occurred
        </p>
        <Link
          to="/"
          className="bg-[#DA0E1A] text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  if (!cartItems?.items?.length) {
    return (
      <div>
        {orderSuccess && orderId && <OrderSuccessModal orderId={orderId} />}
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <p className="text-2xl font-semibold text-gray-600">
            Your cart is empty
          </p>
          <Link
            to="/"
            className="bg-[#DA0E1A] text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = cartItems?.summary?.originalTotal || 0;
  const shipping = cartItems?.summary?.shipping || 0;
  const discount = cartItems?.summary?.discountTotal || 0;
  const total = cartItems?.summary?.finalTotal || 0;
  return (
    <div className="container py-8">
      <div className="flex flex-col lg:flex-row gap-8 container">
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-6">
            Your Cart ({cartItems.items.length} items)
          </h2>
          <div className="space-y-4">
            {cartItems?.items?.map((item) => (
              <div
                key={item.productId}
                className="flex items-center gap-4 p-2 justify-between md:p-4 bg-white rounded-lg shadow md:flex-row flex-col"
              >
                <div className="flex items-center gap-2 bg-white rounded-lg md:py-4">
                  <img
                    src={`${API}/products/photo/${item.productId}`}
                    alt={item.name || "Product"}
                    className="w-24 h-24 object-contain"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold">{item.name}</h3>
                    <p className="text-green-600 font-bold">{item.price} EGP</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => {
                          if (item.count === 1) return;
                          else
                            handleQuantityUpdate(
                              item.productId,
                              Math.max(1, item.count - 1)
                            );
                        }}
                        className="px-2 py-1 border rounded"
                        disabled={
                          isUpdatingQuantity &&
                          item.productId === updatingItemId
                        }
                      >
                        -
                      </button>
                      <span>
                        {isUpdatingQuantity &&
                        item.productId === updatingItemId ? (
                          <CustomLoading color={"#ff0000"} />
                        ) : (
                          item.count
                        )}
                      </span>
                      <button
                        onClick={() =>
                          handleQuantityUpdate(item.productId, item.count + 1)
                        }
                        className="px-2 py-1 border rounded"
                        disabled={
                          isUpdatingQuantity &&
                          item.productId === updatingItemId
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <button
                    onClick={() => handleRemoveItem(item.productId)}
                    className="text-red-500 hover:text-red-700 md:px-4"
                    disabled={
                      isRemovingProduct && item.productId === removingItemId
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1/2">
          <CreateOrder
            setOrderSuccess={setOrderSuccess}
            setOrderId={setOrderId}
          />
        </div>
      </div>
    </div>
  );
}

export default AuthenticatedCart;
